// biome-ignore-all lint/suspicious/noExplicitAny: Supabase client is typed as any throughout the portal domain layer
import { Injectable, inject } from '@angular/core';
import { CountryContext } from '../../../shared/country/country-context.service';
import { CapabilityDefectReporter } from '../../../shared/services/capability-defect-reporter.service';
import { AuthStore } from '../../auth.store';
import { type ChunkProgressReporter, reportChunkProgress } from '../shared/chunk-progress';
import { type EAN, tryEAN } from '../shared/ean';
import { toMasked } from '../shared/masked-ean';
import { mapNonEmpty, type NonEmpty } from '../shared/non-empty';
import { likeLiteral, postgrestQuote } from '../shared/postgrest-filter';
import {
  type DecodeResult,
  type DecodeSuccess,
  decodeBrandPayl,
  decodePayl,
  type Observation,
  type Payl,
  type PaylVendorRecord,
  type SeriesObservation,
} from './observation';
import {
  type BrandCategoryMeta,
  type BrandSeriesResult,
  brandCategoryKey,
  brandKey,
  type CacheBatchResult,
  type CacheKey,
  type CacheRowResult,
  ObservationReadError,
  ObservationRepository,
  type UpdateProbeEntry,
} from './observation-repository';
import { scanVendorChannels } from './price-convention-defects';
import { priceFieldConventionOf } from './vendor-price-conventions';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** The `c_key` a cache key is stored under: a product code masked, a brand key as it is. */
function wireKeyOf(key: string): string {
  const ean = tryEAN(key);
  return ean ? toMasked(ean) : key;
}

/** Every key's wire form, mapped back to the key that was asked for. */
function wireKeysFor(keys: readonly string[]): ReadonlyMap<string, string> {
  return new Map(keys.map((key) => [wireKeyOf(key), key]));
}

// PostgREST's max_rows: a page shorter than this is the last one (.claude/rules/data-access.md).
const PAGE_SIZE = 1000;

// Keys per `.in('c_key', ...)` filter: the request is a GET, so the whole list rides in the URL.
const BATCH_SIZE = 200;

// Bytes of in() and or() filter text per request: the Supabase gateway refuses a request whose URL
// and headers pass 16 KB, and the rest is left for the headers and the fixed query parameters.
const FILTER_BUDGET_BYTES = 12_000;

// The least room a month filter may leave for a readBatch key chunk; below it the read would
// split into hundreds of near single-key requests, so it fails instead.
const MIN_KEY_BUDGET_BYTES = 200;

function parsePayl(raw: unknown): Record<string, Record<string, PaylVendorRecord>> | null {
  if (!raw) return null;
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (obj && typeof obj === 'object')
      return obj as Record<string, Record<string, PaylVendorRecord>>;
    return null;
  } catch {
    return null;
  }
}

/**
 * Last-write-wins date-key merge: later rows in `rows` overwrite earlier ones
 * for the same date key (matches readPortalCacheByKey and readPortalCacheBatch semantics).
 */
function mergeLastWriteWins(rows: any[]): {
  merged: Record<string, Record<string, PaylVendorRecord>>;
  upd: string | null;
} {
  const merged: Record<string, Record<string, PaylVendorRecord>> = {};
  let latestUpd: string | null = null;

  for (const row of rows) {
    const parsed = parsePayl(row.payl);
    if (parsed) {
      for (const dateKey of Object.keys(parsed)) {
        merged[dateKey] = parsed[dateKey];
      }
    }
    if (!latestUpd || (row.upd && new Date(row.upd) > new Date(latestUpd))) {
      if (row.upd) latestUpd = String(row.upd);
    }
  }

  return { merged, upd: latestUpd };
}

// Signal fields: the only fields summed across category slices (PRD D-02).
const SIGNAL_FIELDS = ['qs', 'rev'] as const;
// Observed fields: point-in-time facts that are nonsensical to sum; null on collision.
const OBSERVED_FIELDS = ['p', 'lp', 'q'] as const;

/**
 * Additive signal merge: matches readPortalCacheByKeyWithCategory (bc- brand reads).
 * On (date, vendor) collision across category slices, only `qs` and `rev` are summed;
 * observed fields (`p`, `lp`, `q`) become null on the merged output (PRD D-02).
 * Non-colliding (date, vendor) pairs pass through unchanged.
 */
function mergeAdditive(rows: any[]): {
  merged: Record<string, Record<string, PaylVendorRecord>>;
  upd: string | null;
} {
  const merged: Record<string, Record<string, PaylVendorRecord>> = {};
  let latestUpd: string | null = null;

  for (const row of rows) {
    if (row.upd && (!latestUpd || new Date(row.upd) > new Date(latestUpd))) {
      latestUpd = String(row.upd);
    }

    const parsed = parsePayl(row.payl);
    if (!parsed) continue;

    for (const dateKey of Object.keys(parsed)) {
      const dayData = parsed[dateKey];
      if (!merged[dateKey]) {
        merged[dateKey] = { ...dayData };
        continue;
      }

      for (const vendorKey of Object.keys(dayData)) {
        const incoming = dayData[vendorKey] as Record<string, unknown>;
        if (!merged[dateKey][vendorKey]) {
          merged[dateKey][vendorKey] = { ...incoming } as PaylVendorRecord;
          continue;
        }

        const existing = merged[dateKey][vendorKey] as Record<string, unknown>;
        for (const field of SIGNAL_FIELDS) {
          const a = typeof existing[field] === 'number' ? (existing[field] as number) : 0;
          const b = typeof incoming[field] === 'number' ? (incoming[field] as number) : 0;
          (existing as Record<string, number>)[field] = a + b;
        }
        for (const field of OBSERVED_FIELDS) {
          existing[field] = null;
        }
      }
    }
  }

  return { merged, upd: latestUpd };
}

const MONTH_FORMAT_RE = /^\d{4}-\d{2}$/;

// Category values are `_`-joined display names round-tripped from portal_cache.category
// ("Kosttillskott_Vitaminer och mineraler"): whitespace is the normal case, and `_` is a
// LIKE single-character wildcard. Quoting is the PostgREST logic-tree grammar's own escape
// hatch, so nothing in the DB's alphabet needs rejecting; only an empty value is a defect.
function assertNonEmptyCategory(cat: string): string {
  if (!cat) {
    throw new Error('[ObservationRepository] empty category value in readBrandCategorySeries');
  }
  return cat;
}

// A prefix under another one is already matched by its `like`, and read twice once the two land in
// different prefix chunks (.claude/rules/data-access.md, bc- slices are disjoint).
function topmostPrefixes(prefixes: NonEmpty<string>): NonEmpty<string> {
  const all = mapNonEmpty(prefixes, assertNonEmptyCategory);
  // The lexicographically first prefix has no shorter prefix in the set: it is always topmost.
  const first = all.reduce((a, b) => (b < a ? b : a));
  const rest = [...new Set(all)]
    .filter((p) => p !== first && !all.some((q) => p.startsWith(`${q}_`)))
    .sort();
  return [first, ...rest];
}

// supabase-js spells every filter through URLSearchParams, so that is what a value costs.
function urlBytes(value: string): number {
  return new URLSearchParams({ v: value }).toString().length - 2;
}

const SEPARATOR_BYTES = urlBytes(',');

// Consecutive runs of the items, each spelling at most `budget` bytes and `maxItems` items.
function chunkByBytes(
  items: readonly string[],
  budget: number,
  maxItems: number
): readonly (readonly string[])[] {
  const out: string[][] = [];
  let current: string[] = [];
  let bytes = 0;
  for (const item of items) {
    const cost = urlBytes(item) + SEPARATOR_BYTES;
    if (cost > budget) {
      throw new Error(
        `[ObservationRepository] a ${cost}-byte filter value cannot fit a ${budget}-byte request`
      );
    }
    if (current.length > 0 && (bytes + cost > budget || current.length >= maxItems)) {
      out.push(current);
      current = [];
      bytes = 0;
    }
    current.push(item);
    bytes += cost;
  }
  if (current.length > 0) out.push(current);
  return out;
}

function chunkBytes(chunk: readonly string[]): number {
  return chunk.reduce((sum, item) => sum + urlBytes(item) + SEPARATOR_BYTES, 0);
}

// Every way of taking one chunk from each group, in order.
function combinationsOf(
  groups: readonly (readonly (readonly string[])[])[]
): readonly (readonly (readonly string[])[])[] {
  return groups.reduce<(readonly (readonly string[])[])[]>(
    (combos, chunks) => combos.flatMap((combo) => chunks.map((chunk) => [...combo, chunk])),
    [[]]
  );
}

// The node itself, or any descendant path under it (`<node>_<child>...`).
function categoryOrExpr(cat: string): string {
  return `category.eq.${postgrestQuote(cat)},category.like.${postgrestQuote(`${likeLiteral(cat)}\\_%`)}`;
}

function monthCondition(s: string): string {
  if (!MONTH_FORMAT_RE.test(String(s))) {
    throw new Error(`[ObservationRepository] invalid month format: "${s}" (expected YYYY-MM)`);
  }
  const [ys, ms] = s.split('-');
  return `and(y_ref.eq.${ys},m_ref.eq.${ms})`;
}

// The status-marker partition: marker rows are the only rows stored under y_ref = m_ref = 'NA', and
// they carry the freshness of a product with no series, so a windowed batch read always takes them.
const NA_CONDITION = 'and(y_ref.eq.NA,m_ref.eq.NA)';

function buildMonthOrExpr(monthsYM: readonly string[]): string | null {
  const pairs = monthsYM.map(monthCondition);
  return pairs.length > 0 ? pairs.join(',') : null;
}

function toRowResult(
  key: CacheKey,
  merged: Record<string, Record<string, PaylVendorRecord>>,
  upd: string | null
): CacheRowResult | null {
  if (Object.keys(merged).length === 0) return null;

  const payl = merged as Payl;
  const keyStr = String(key);
  // Brand / brand-category keys carry no EAN; decode via brand-grain entry point.
  const observations: readonly DecodeResult[] =
    keyStr.startsWith('b-') || keyStr.startsWith('bc-')
      ? decodeBrandPayl(payl)
      : decodePayl(key as EAN, payl);

  for (const result of observations) {
    if (result.kind === 'err') {
      console.error(
        `[ObservationRepository] decode failure for ${keyStr} day=${result.day} vendor=${result.vendorKey}: ${result.reason}`
      );
    }
  }

  return { upd, observations, hasPayload: true };
}

/** Observation is assignable to both SeriesObservation and ResolvedVendor. */
function decodedObservations(results: readonly DecodeResult[]): readonly Observation[] {
  return results.filter((r): r is DecodeSuccess => r.kind === 'ok').map((r) => r.observation);
}

// ---------------------------------------------------------------------------
// Supabase implementation
// ---------------------------------------------------------------------------

@Injectable()
export class SupabaseObservationRepository extends ObservationRepository {
  private readonly authStore = inject(AuthStore);
  private readonly countryContext = inject(CountryContext);
  private readonly defectReporter = inject(CapabilityDefectReporter);

  private get table(): string {
    return this.countryContext.config.cacheTable;
  }

  private reportCapabilityDefects(decoded: readonly DecodeResult[]): void {
    const { seen, defects } = scanVendorChannels(
      decodedObservations(decoded),
      priceFieldConventionOf
    );
    this.defectReporter.report(this.table, seen);
    this.defectReporter.reportDefects(this.table, defects);
  }

  /** One check for a single-row read; loop-shaped reads check once after their loop. */
  private withDefectsReported(result: CacheRowResult | null): CacheRowResult | null {
    if (result) this.reportCapabilityDefects(result.observations);
    return result;
  }

  private client(): Promise<any> {
    return this.authStore.getSupabaseClient();
  }

  async readByKey(key: CacheKey, monthsYM?: readonly string[]): Promise<CacheRowResult | null> {
    const orExpr = monthsYM && monthsYM.length > 0 ? buildMonthOrExpr(monthsYM) : null;
    const supabase = await this.client();
    let q = supabase
      .from(this.table)
      .select('c_key,payl,upd')
      .in('c_key', [...wireKeysFor([String(key)]).keys()]);
    if (orExpr) q = q.or(orExpr);

    const { data, error } = await q;
    if (error) {
      console.error('[ObservationRepository] readByKey error:', error);
      return null;
    }

    const { merged, upd } = mergeLastWriteWins(data ?? []);
    return this.withDefectsReported(toRowResult(key, merged, upd));
  }

  // Each or() group's conditions are disjoint, so a row matches one chunk combination: read once.
  private async readKeyPages(
    columns: string,
    keys: readonly string[],
    orGroups: readonly NonEmpty<string>[],
    signal: AbortSignal | undefined
  ): Promise<readonly any[]> {
    const groupShare = FILTER_BUDGET_BYTES / (orGroups.length + 2);
    const groupChunks = orGroups.map((group) => chunkByBytes(group, groupShare, Infinity));
    const groupBytes = groupChunks.reduce(
      (sum, chunks) => sum + Math.max(...chunks.map(chunkBytes)),
      0
    );
    const keyChunks = chunkByBytes(keys, FILTER_BUDGET_BYTES - groupBytes, BATCH_SIZE);
    const supabase = await this.client();
    const rows: any[] = [];
    for (const batch of keyChunks) {
      for (const combo of combinationsOf(groupChunks)) {
        for (let offset = 0; ; offset += PAGE_SIZE) {
          const narrowed = combo.reduce(
            (q, chunk) => q.or(chunk.join(',')),
            supabase.from(this.table).select(columns).in('c_key', batch)
          );
          const { data, error } = await narrowed
            // portal_cache_pkey (c_key, y_ref, m_ref, cat_key), in the key's own column order.
            .order('c_key')
            .order('y_ref')
            .order('m_ref')
            .order('cat_key')
            .range(offset, offset + PAGE_SIZE - 1)
            .abortSignal(signal);
          if (error) throw new ObservationReadError(this.table, error);
          for (const row of data ?? []) rows.push(row);
          if (!data || data.length < PAGE_SIZE) break;
        }
      }
    }
    return rows;
  }

  // Rows grouped under the slug their key was built from, merged once per slug.
  private mergedPerSlug(
    rows: readonly any[],
    slugByKey: ReadonlyMap<string, string>,
    merge: typeof mergeAdditive
  ): ReadonlyMap<string, CacheRowResult> {
    const rowsByKey = new Map<string, any[]>();
    for (const row of rows) {
      const key = String(row?.c_key ?? '');
      if (!slugByKey.has(key)) continue;
      const keyRows = rowsByKey.get(key) ?? [];
      keyRows.push(row);
      rowsByKey.set(key, keyRows);
    }
    const out = new Map<string, CacheRowResult>();
    const decoded: DecodeResult[] = [];
    for (const [key, slug] of slugByKey) {
      const keyRows = rowsByKey.get(key);
      if (!keyRows) continue;
      const { merged, upd } = merge(keyRows);
      const result = toRowResult(key as CacheKey, merged, upd);
      if (!result) continue;
      out.set(slug, result);
      decoded.push(...result.observations);
    }
    this.reportCapabilityDefects(decoded);
    return out;
  }

  async readBrandCategorySeries(
    brandSlugs: readonly string[],
    categoryPrefixes: NonEmpty<string>,
    monthsYM: NonEmpty<string>,
    signal?: AbortSignal
  ): Promise<ReadonlyMap<string, CacheRowResult>> {
    const slugByKey = new Map(brandSlugs.map((slug) => [String(brandCategoryKey(slug)), slug]));
    const keys = [...slugByKey.keys()].sort();
    // The topmost prefixes are disjoint subtrees and the months disjoint partitions.
    const rows = await this.readKeyPages(
      'c_key,payl',
      keys,
      [
        mapNonEmpty(topmostPrefixes(categoryPrefixes), categoryOrExpr),
        mapNonEmpty(monthsYM, monthCondition),
      ],
      signal
    );
    return this.mergedPerSlug(rows, slugByKey, mergeAdditive);
  }

  async readWholeBrandSeries(
    brandSlugs: readonly string[],
    monthsYM: NonEmpty<string>,
    signal?: AbortSignal
  ): Promise<ReadonlyMap<string, CacheRowResult>> {
    const slugByKey = new Map(brandSlugs.map((slug) => [String(brandKey(slug)), slug]));
    const keys = [...slugByKey.keys()].sort();
    const rows = await this.readKeyPages(
      'c_key,payl',
      keys,
      [mapNonEmpty(monthsYM, monthCondition)],
      signal
    );
    return this.mergedPerSlug(rows, slugByKey, mergeLastWriteWins);
  }

  async readBatch(
    keys: readonly CacheKey[],
    monthsYM?: NonEmpty<string>,
    onProgress?: ChunkProgressReporter,
    signal?: AbortSignal
  ): Promise<CacheBatchResult> {
    const list = Array.from(new Set(keys.map(String).filter(Boolean))) as CacheKey[];
    const out = new Map<CacheKey, CacheRowResult>();
    if (!list.length) return out;

    // portal_cache has multiple rows per EAN (month partitions via y_ref/m_ref plus
    // category rows). A 200-EAN chunk can exceed PostgREST's max_rows (~1000) and
    // silently truncate. We page through results until a short page signals the end.
    const MAX_CONCURRENT = 5;
    const wireKeys = wireKeysFor(list);
    const askedKeys = [...wireKeys.keys()].sort();
    // The month or() shares the URL with the key list, so it shrinks the key chunk.
    const monthExpr = monthsYM
      ? [...mapNonEmpty(monthsYM, monthCondition), NA_CONDITION].join(',')
      : null;
    const keyBudget = FILTER_BUDGET_BYTES - (monthExpr ? urlBytes(monthExpr) : 0);
    if (keyBudget < MIN_KEY_BUDGET_BYTES) {
      throw new Error(
        `[ObservationRepository] readBatch: month filter leaves no room for keys (${monthsYM?.length} months)`
      );
    }
    const batches = chunkByBytes(askedKeys, keyBudget, BATCH_SIZE);

    const allRows: any[] = [];
    const supabase = await this.client();
    let keysSent = 0;
    for (let w = 0; w < batches.length; w += MAX_CONCURRENT) {
      signal?.throwIfAborted();
      const wave = batches.slice(w, w + MAX_CONCURRENT);
      const pageResults = await Promise.all(
        wave.map(async (batch) => {
          const batchRows: any[] = [];
          let offset = 0;
          while (true) {
            signal?.throwIfAborted();
            const filtered = supabase.from(this.table).select('c_key,payl,upd').in('c_key', batch);
            const { data, error } = await (monthExpr ? filtered.or(monthExpr) : filtered)
              // portal_cache_pkey (c_key, y_ref, m_ref, cat_key), in the key's own column order.
              .order('c_key')
              .order('y_ref')
              .order('m_ref')
              .order('cat_key')
              .range(offset, offset + PAGE_SIZE - 1)
              .abortSignal(signal);
            if (error) {
              // postgrest-js resolves an aborted fetch as an error with no code.
              if (signal?.aborted) throw signal.reason;
              throw new Error(`[ObservationRepository] readBatch: ${error.message}`, {
                cause: error,
              });
            }
            if (data && data.length > 0) batchRows.push(...data);
            if (!data || data.length < PAGE_SIZE) break;
            offset += PAGE_SIZE;
          }
          return batchRows;
        })
      );
      for (const rows of pageResults) {
        allRows.push(...rows);
      }
      keysSent += wave.reduce((sum, batch) => sum + batch.length, 0);
      reportChunkProgress(onProgress, keysSent, askedKeys.length);
    }

    const byKey: Record<string, any[]> = {};
    for (const row of allRows) {
      // Grouped under the key the caller asked for, whichever form the row is stored in.
      const k = wireKeys.get(String(row.c_key ?? ''));
      if (!k) continue;
      if (!byKey[k]) byKey[k] = [];
      byKey[k].push(row);
    }

    const decoded: DecodeResult[] = [];
    for (const key of list) {
      const rows = byKey[key] ?? [];
      if (!rows.length) continue;
      const { merged, upd } = mergeLastWriteWins(rows);
      const result = toRowResult(key, merged, upd);
      if (!result) continue;
      out.set(key, result);
      decoded.push(...result.observations);
    }
    this.reportCapabilityDefects(decoded);

    return out;
  }

  async loadCategoryMetaOfBrands(
    brandSlugs: readonly string[],
    signal?: AbortSignal
  ): Promise<ReadonlyMap<string, readonly BrandCategoryMeta[]>> {
    const slugByKey = new Map<string, string>(
      brandSlugs.map((slug) => [brandCategoryKey(slug), slug])
    );
    const keys = [...slugByKey.keys()].sort();
    const out = new Map<string, BrandCategoryMeta[]>();
    for (const row of await this.readKeyPages('c_key,category,n_count', keys, [], signal)) {
      const slug = slugByKey.get(String(row?.c_key ?? ''));
      const category = String(row?.category ?? '').trim();
      if (!slug || !category) continue;
      const rows = out.get(slug) ?? [];
      rows.push({ category, nCount: Number(row?.n_count ?? 0) });
      out.set(slug, rows);
    }
    return out;
  }

  async probeUpdates(
    eans: readonly EAN[],
    signal?: AbortSignal
  ): Promise<readonly UpdateProbeEntry[]> {
    if (!eans.length) return [];
    const supabase = await this.client();
    const wireKeys = wireKeysFor(eans);
    const askedKeys = [...wireKeys.keys()].sort();
    const rows: any[] = [];
    let offset = 0;
    while (true) {
      const { data, error } = await supabase
        .from(this.table)
        .select('c_key,y_ref,m_ref,upd')
        .in('c_key', askedKeys)
        .order('c_key')
        .order('y_ref')
        .order('m_ref')
        .order('cat_key')
        .range(offset, offset + PAGE_SIZE - 1)
        .abortSignal(signal);

      if (error) {
        // postgrest-js resolves an aborted fetch as an error with no code.
        if (signal?.aborted) throw signal.reason;
        throw new Error(`[ObservationRepository] probeUpdates: ${error.message}`, {
          cause: error,
        });
      }
      if (data && data.length > 0) rows.push(...data);
      if (!data || data.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    const entries: UpdateProbeEntry[] = [];
    for (const row of rows) {
      // Reported under the key the caller probed: it indexes the answer by that value.
      const cKey = wireKeys.get(String(row.c_key ?? ''));
      if (!cKey) continue;
      const monthYM = row.y_ref === 'NA' ? 'NA' : `${row.y_ref}-${row.m_ref}`;
      entries.push({ cKey, monthYM, upd: row.upd ? String(row.upd) : null });
    }
    return entries;
  }

  async readBrandSeries(
    pbId: string,
    monthsYM?: readonly string[],
    signal?: AbortSignal
  ): Promise<BrandSeriesResult> {
    const orExpr = monthsYM && monthsYM.length > 0 ? buildMonthOrExpr(monthsYM) : null;
    const supabase = await this.client();
    let q = supabase.from(this.table).select('*').eq('c_key', `bps-${pbId}`).abortSignal(signal);
    if (orExpr) q = q.or(orExpr);

    const { data, error } = await q;
    if (error) throw new ObservationReadError(this.table, error);

    const brandPayloads = new Map<string, Record<string, Record<string, PaylVendorRecord>>>();

    for (const row of data ?? []) {
      if (!row.payl) continue;
      let parsed: unknown;
      try {
        parsed = typeof row.payl === 'string' ? JSON.parse(row.payl) : row.payl;
      } catch {
        continue;
      }
      if (!parsed || typeof parsed !== 'object') continue;

      for (const brandSlug of Object.keys(parsed as object)) {
        const brandData = (parsed as Record<string, unknown>)[brandSlug];
        if (!brandData || typeof brandData !== 'object') continue;

        if (!brandPayloads.has(brandSlug)) {
          brandPayloads.set(brandSlug, {});
        }
        const existing = brandPayloads.get(brandSlug)!;
        for (const dateKey of Object.keys(brandData as object)) {
          existing[dateKey] = (brandData as Record<string, Record<string, PaylVendorRecord>>)[
            dateKey
          ];
        }
      }
    }

    const result = new Map<string, readonly SeriesObservation[]>();
    const decoded: DecodeResult[] = [];
    for (const [brandSlug, merged] of brandPayloads) {
      if (Object.keys(merged).length === 0) continue;
      const decodeResults = decodeBrandPayl(merged as Payl);
      for (const r of decodeResults) {
        if (r.kind === 'err') {
          console.error(
            `[ObservationRepository] readBrandSeries decode failure for ${brandSlug} day=${r.day} vendor=${r.vendorKey}: ${r.reason}`
          );
        }
      }
      decoded.push(...decodeResults);
      const obs = decodedObservations(decodeResults);
      if (obs.length > 0) result.set(brandSlug, obs);
    }
    this.reportCapabilityDefects(decoded);
    return result;
  }

  invalidate(_keys: readonly CacheKey[]): void {}
}
